import { Fragment } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { User, Workorder } from '@certhon/models';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Check, AlertCircle, MapPinOff } from 'lucide-react';
import { useStackNavigate } from '@/app/navigation/stack-links';
import { RouteSegment } from './RouteSegment';
import { HQRouteSegment } from '../../components/HQRouteSegment';
import { WorkorderCard } from '../../shared';
import { RouteResponse } from '../dayplanning.types';

interface WorkorderRouteError {
  incomingError?: string;
  outgoingError?: string;
}

interface DayPlanningWorkorderListProps {
  /** Workorders to display */
  workorders: Workorder[];
  /** Whether in multi-technician view mode */
  isMultiTechView: boolean;
  /** Whether routes are loading */
  isLoadingRoutes: boolean;
  /** Map of technician colors by user ID */
  technicianColors: Map<number, string>;
  /** Single technician color (for single-tech view) */
  singleTechColor?: string;
  /** Map of workorder recnum to per-technician index */
  perTechIndexMap: Map<number, number>;
  /** Map of user ID to user details */
  userMap: Map<number, User>;
  /** Map of workorder recnum to route errors */
  routeErrors: Map<number, WorkorderRouteError>;
  /** Callback when drag ends (manual reorder) */
  onDragEnd: (event: DragEndEvent) => void;
  /** Callback when arrive time changes */
  onArriveTimeChange: (workorder: Workorder, time: string | null) => void;
  /** Callback when route option changes */
  onRouteOptionChange: (
    workorder: Workorder,
    option: 'direct' | 'via_hq',
  ) => void;
  /** Function to get route for a workorder by index */
  getRouteForWorkorder: (index: number) => RouteResponse | null;
  /** Leading route (HQ to first workorder) */
  leadingRoute: RouteResponse | null;
  /** Trailing route (last workorder to HQ) */
  trailingRoute: RouteResponse | null;
  /** Path of the hosting panel; cards drill down below it */
  linkBase: string;
}

/**
 * Day Planning Workorder List Component
 *
 * Displays the list of workorders with:
 * - Drag-and-drop reordering (single-tech admin mode)
 * - Route segments between workorders
 * - HQ segments (leading and trailing)
 * - Read-only multi-technician view with color coding
 */
export function DayPlanningWorkorderList({
  workorders,
  isMultiTechView,
  isLoadingRoutes,
  technicianColors,
  singleTechColor,
  perTechIndexMap,
  userMap,
  routeErrors,
  onDragEnd,
  onArriveTimeChange,
  onRouteOptionChange,
  getRouteForWorkorder,
  leadingRoute,
  trailingRoute,
  linkBase,
}: DayPlanningWorkorderListProps) {
  const navigate = useStackNavigate();

  // DnD sensors for single-tech editable view
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Single-technician editable view with drag-drop
  if (!isMultiTechView) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={workorders.map((wo) => wo.recnum)}
          strategy={verticalListSortingStrategy}
        >
          <div
            className="space-y-1"
            role="list"
            aria-live="polite"
            aria-label="Workorder list"
          >
            {/* Leading route: HQ -> first workorder */}
            <HQRouteSegment
              route={leadingRoute}
              direction="from_hq"
              isLoading={isLoadingRoutes}
            />

            {workorders.map((wo, idx) => (
              <Fragment key={wo.recnum}>
                <div role="listitem">
                  {/* Route segment (shown between cards, not before first) */}
                  {idx > 0 && (
                    <RouteSegment
                      route={getRouteForWorkorder(idx)}
                      routeOption={wo.route_option}
                      onRouteOptionChange={(opt) =>
                        onRouteOptionChange(wo, opt)
                      }
                      isLoading={isLoadingRoutes}
                    />
                  )}

                  {/* Workorder card */}
                  <WorkorderCard
                    mode="editable"
                    workorder={wo}
                    index={idx}
                    onArriveTimeChange={(time) => onArriveTimeChange(wo, time)}
                    technicianColor={singleTechColor}
                    routeError={routeErrors.get(wo.recnum)}
                    linkBase={linkBase}
                  />
                </div>
              </Fragment>
            ))}

            {/* Trailing route: last workorder -> HQ */}
            <HQRouteSegment
              route={trailingRoute}
              direction="to_hq"
              isLoading={isLoadingRoutes}
            />
          </div>
        </SortableContext>
      </DndContext>
    );
  }

  // Multi-technician read-only view
  return (
    <div
      className="space-y-1"
      role="list"
      aria-label="Multi-technician workorder list"
    >
      {workorders.map((wo, idx) => {
        const techUser = wo.assigned_user_id
          ? userMap.get(wo.assigned_user_id)
          : null;
        const techColor = wo.assigned_user_id
          ? technicianColors.get(wo.assigned_user_id)
          : undefined;
        const woRouteError = routeErrors.get(wo.recnum);
        const hasRouteError = !!(
          woRouteError?.incomingError || woRouteError?.outgoingError
        );
        const hasAddress = !!(
          wo.location_address && wo.location_address.trim()
        );
        const detailsPath = `${linkBase}/workorders/${wo.recnum}`;
        const openDetails = () => navigate({ to: detailsPath });

        return (
          <div
            key={wo.recnum}
            role="button"
            tabIndex={0}
            aria-label={`W${wo.id}, ${wo.customer_name ?? ''}`}
            onClick={(event) => {
              // Tooltips and links inside the row keep their own behaviour
              if ((event.target as HTMLElement).closest('button, a')) return;
              openDetails();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openDetails();
              }
            }}
            className={`bg-card hover:bg-accent/50 cursor-pointer rounded-lg border p-3 text-sm
            transition-colors ${wo.completed ? 'opacity-70' : ''} ${
            hasRouteError
                ? 'border-red-500/50'
                : !hasAddress
                  ? 'border-amber-500/50'
                  : ''
            }`}
          >
            <div className="flex items-center gap-2">
              {/* Index badge with completion indicator */}
              <div className="relative">
                <span
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs
                    font-semibold text-white"
                  style={{ backgroundColor: techColor }}
                >
                  {perTechIndexMap.get(wo.recnum) ?? idx + 1}
                </span>
                {wo.completed && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className="ring-card absolute -top-0.5 -right-0.5 flex h-3 w-3 items-center justify-center
                            rounded-full bg-emerald-500 ring-2 dark:bg-emerald-400"
                        >
                          <Check
                            className="h-2 w-2 text-white"
                            strokeWidth={3}
                          />
                        </div>
                      </TooltipTrigger>
                      {wo.date_completed && (
                        <TooltipContent side="right">
                          <p className="text-xs">
                            Completed{' '}
                            {new Date(wo.date_completed).toLocaleTimeString(
                              'nl-NL',
                              {
                                hour: '2-digit',
                                minute: '2-digit',
                              },
                            )}
                          </p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              {/* Route error indicator */}
              {hasRouteError && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <MapPinOff className="h-4 w-4 shrink-0 text-red-600 dark:text-red-500" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-medium">Route calculation failed</p>
                      {woRouteError?.incomingError && (
                        <p className="text-xs opacity-90">
                          {woRouteError.incomingError}
                        </p>
                      )}
                      {woRouteError?.outgoingError && (
                        <p className="mt-1 text-xs opacity-90">
                          Return: {woRouteError.outgoingError}
                        </p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {/* Warning icon for missing address */}
              {!hasAddress && !hasRouteError && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AlertCircle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Missing address - excluded from route calculation</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <strong>WO-{wo.id}</strong>
              <span className="text-muted-foreground">{wo.customer_name}</span>
              {techUser && (
                <span className="text-muted-foreground ml-auto text-xs">
                  {techUser.name || techUser.loginname}
                </span>
              )}
            </div>
            {wo.location_address && (
              <div className="text-muted-foreground mt-1 text-xs">
                {wo.location_address}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
